import { useQuery, useQueryClient } from "@tanstack/react-query";
import { docMimeOf, type DocumentDetailDto, type DocumentTypeDto, type DossierDto } from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { putToStorage } from "../../lib/upload";
import type { PresignResponse } from "@klantpunt/shared";

export const dossierKey = (buildingId: string) => ["dossier", buildingId] as const;
export const documentKey = (id: string) => ["document", id] as const;
export const docTypesKey = ["document-types"] as const;

export function useDossier(buildingId: string, enabled = true) {
  return useQuery({
    queryKey: dossierKey(buildingId),
    queryFn: () => api<DossierDto>(`/buildings/${buildingId}/dossier`),
    enabled: enabled && !!buildingId,
  });
}

export function useDocument(id: string | null) {
  return useQuery({
    queryKey: documentKey(id ?? ""),
    queryFn: () => api<DocumentDetailDto>(`/documents/${id}`),
    enabled: !!id,
  });
}

export function useDocTypes() {
  return useQuery({ queryKey: docTypesKey, queryFn: () => api<DocumentTypeDto[]>("/document-types") });
}

/** Na een wijziging: dossier, het document, het gebouw (tellers bij assets) en open asset-panelen opnieuw laden. */
export function useDossierRefresh(buildingId: string) {
  const qc = useQueryClient();
  return async (docId?: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: dossierKey(buildingId) }),
      docId ? qc.invalidateQueries({ queryKey: documentKey(docId) }) : null,
      docId ? qc.invalidateQueries({ queryKey: ["logbook", "document", docId] }) : null,
      qc.invalidateQueries({ queryKey: ["asset"] }),
      qc.invalidateQueries({ queryKey: ["building", buildingId] }),
      qc.invalidateQueries({ queryKey: ["dossiers"] }),
    ]);
  };
}

export const fileUrl = (fileId: string) => `/api/files/${fileId}/url`;

export const fmtDate = (iso: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("nl-NL") : "";

export const fmtSize = (b: number | null) =>
  b == null ? "" : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} kB` : `${(b / 1048576).toFixed(1)} MB`;

export const today = () => new Date().toISOString().slice(0, 10);

/** Vingerafdruk (SHA-256) om dubbele uploads te herkennen; null als de browser het niet kan. */
export async function sha256Of(file: Blob): Promise<string | null> {
  try {
    if (!crypto.subtle || file.size > 100 * 1024 * 1024) return null;
    const buf = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

/** Document uploaden: presign (soort "document") → opslag → bevestigen. Geeft het bestand-id. */
export async function uploadDocumentFile(file: Blob, name: string): Promise<string> {
  const id = crypto.randomUUID();
  const mime = docMimeOf(name, file.type);
  if (!mime) throw new ApiError(400, "unsupported_type", "Onbekend bestandstype");
  const presign = await api<PresignResponse>("/files/presign", {
    method: "POST",
    body: { id, kind: "document", mime, size: file.size, name },
  });
  await putToStorage(presign, file);
  await api(`/files/${id}/confirm`, { method: "POST", body: {} });
  return id;
}
